/**
 * Local menu query hook for the shell adapter.
 *
 * Follows Outbound's pattern exactly:
 *   1. BASE_MENU defines every possible menu item.
 *   2. A Central-role / capability pass computes the baseline visibility.
 *   3. When the Central role is `member` AND a testing-mode persona is active,
 *      an APP_ROLE_MENU_ITEMS whitelist INTERSECTS with the baseline. This can
 *      only narrow visibility, never expand it.
 *
 * Access control lives in the backend — this hook only controls sidebar
 * visibility so roles see a coherent set of features.
 */

import { useMemo } from 'react';
import type { MenuQueryResult } from '@levelup/ui-shell';
import { getLegacyTestPersona } from '../lib/centralTestingMode';
import { usePermissions } from './usePermissions';

interface BaseMenuItem {
  menuKey: string;
  label: string;
  order: number;
  /** Capability required for the item to be considered by the Central-role pass. */
  requiresCapability: string | null;
}

const BASE_MENU: BaseMenuItem[] = [
  { menuKey: 'dashboard', label: 'Dashboard', order: 1, requiresCapability: null },
  { menuKey: 'agents', label: 'Analysis', order: 2, requiresCapability: 'agents:write' },
  { menuKey: 'recommendations', label: 'Recommendations', order: 3, requiresCapability: 'recommendations:write' },
  { menuKey: 'plans', label: 'Action Plans', order: 4, requiresCapability: 'plans:write' },
  { menuKey: 'alerts', label: 'Alerts', order: 5, requiresCapability: 'alerts:write' },
  { menuKey: 'chat', label: 'Chat', order: 6, requiresCapability: 'chat:write' },
  { menuKey: 'settings', label: 'Settings', order: 99, requiresCapability: null },
];

/**
 * Persona → allowed menu keys. Only applies when the Central role is `member`
 * AND a testing-mode persona is active. Narrows but never expands.
 *
 * Mirrors the persona bundles in packages/shared/src/types/personas.ts:
 *   Operator    → broad member access (all menu items)
 *   Analyst     → Recommendations + Action Plans + Chat (no Analysis/Alerts)
 *   Contributor → Chat only (plus always-on tabs)
 */
const PERSONA_MENU_ITEMS: Record<string, ReadonlySet<string>> = {
  'Legacy Operator': new Set([
    'dashboard', 'agents', 'recommendations', 'plans', 'alerts', 'chat', 'settings',
  ]),
  'Legacy Analyst': new Set([
    'dashboard', 'recommendations', 'plans', 'chat', 'settings',
  ]),
  'Legacy Contributor': new Set([
    'dashboard', 'chat', 'settings',
  ]),
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useGetMenuQuery(_workspaceId: string, _options?: { skip?: boolean }): MenuQueryResult {
  const { can } = usePermissions();

  const data = useMemo<MenuQueryResult['data']>(() => {
    // Persona whitelist: applies whenever a testing-mode persona is set,
    // regardless of the currently-reported Central role. The tester
    // explicitly picked a persona, so honor it even if Central's
    // switch-context hasn't reflected the role override yet.
    const persona = getLegacyTestPersona();
    const personaWhitelist = persona ? PERSONA_MENU_ITEMS[persona] ?? null : null;

    const menu = BASE_MENU
      .filter((item) => {
        // Pass 1 — Central / role capability (can() already handles persona
        // override internally, but the whitelist below is what actually
        // differentiates Analyst from Contributor even when caps say yes).
        const centralAllows =
          item.requiresCapability == null || can(item.requiresCapability);
        if (!centralAllows) return false;

        // Pass 2 — persona whitelist intersect (members only).
        if (personaWhitelist && !personaWhitelist.has(item.menuKey)) return false;

        return true;
      })
      .map((item) => ({
        menuKey: item.menuKey,
        label: item.label,
        order: item.order,
        isVisible: true,
        permissions: { canView: true },
      }));

    return { menu };
  }, [can]);

  return { data, isLoading: false };
}
